import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import { Recipe } from "../types/mealPlan";
import { getPageCache, setPageCache } from "../utils/pageCache";
import { SUMMARY_VIEW_PARAM } from "../utils/recipeSummary";

// sampleRecipes is absent when only the count has been fetched (the wizard's
// mode step), so the dashboard can tell it still has no collage to show.
type CookbookSummary = { cookbookCount: number; sampleRecipes?: Recipe[] };

// The dashboard collage picks 3 of these; the newest dozen keeps some
// visit-to-visit variety without downloading the whole cookbook.
const SAMPLE_SIZE = 12;

type UseRecipesOptions = {
  /** Also fetch a small sample of recent recipes (the dashboard collage). */
  withSamples?: boolean;
};

export const useRecipes = ({ withSamples = true }: UseRecipesOptions = {}) => {
  const { user } = useAuth();
  // Seed from the last summary this session fetched so a remount (e.g. back
  // navigation to the dashboard) renders content immediately and the fetch
  // below becomes a silent background refresh.
  const cacheKey = user ? `cookbook-summary:${user.uid}` : null;
  const seeded = getPageCache<CookbookSummary>(cacheKey);
  const [cookbookCount, setCookbookCount] = useState<number>(seeded?.cookbookCount ?? 0);
  const [sampleRecipes, setSampleRecipes] = useState<Recipe[]>(seeded?.sampleRecipes ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCookbookData = useCallback(async (): Promise<void> => {
    if (!user) {
      setCookbookCount(0);
      setSampleRecipes([]);
      return;
    }

    const key = `cookbook-summary:${user.uid}`;
    const cached = getPageCache<CookbookSummary>(key);
    const hasContent = !!cached && (!withSamples || !!cached.sampleRecipes);
    if (!hasContent) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [countResponse, recipesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cookbook/count`, { headers }),
        withSamples
          ? fetch(`${process.env.REACT_APP_API_URL}/api/recipes?${SUMMARY_VIEW_PARAM}&limit=${SAMPLE_SIZE}`, { headers })
          : Promise.resolve(null),
      ]);

      if (!countResponse.ok || (recipesResponse && !recipesResponse.ok)) {
        throw new Error("Failed to fetch cookbook data");
      }

      const [countData, recipesData] = await Promise.all([
        countResponse.json(),
        recipesResponse ? recipesResponse.json() : Promise.resolve(null),
      ]);

      setCookbookCount(countData.count);
      const samples: Recipe[] | undefined = recipesData
        ? recipesData.recipes || []
        : getPageCache<CookbookSummary>(key)?.sampleRecipes;
      if (samples) {
        setSampleRecipes(samples);
      }
      setPageCache(key, { cookbookCount: countData.count, sampleRecipes: samples });
    } catch (error: any) {
      console.error("Error fetching cookbook data:", error);
      // A failed background refresh keeps the seeded summary on screen;
      // only with nothing to show does the error surface
      if (!hasContent) {
        setError(error.message);
        setCookbookCount(0);
        setSampleRecipes([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user, withSamples]);

  // Fetch data when user changes
  useEffect(() => {
    if (user) {
      fetchCookbookData();
    } else {
      setCookbookCount(0);
      setSampleRecipes([]);
    }
  }, [user, fetchCookbookData]);

  return {
    cookbookCount,
    sampleRecipes,
    loading,
    error,
    refetchCookbookData: fetchCookbookData,
  };
};
